import { useEffect, useState } from 'react';

import { Modal } from '../../../../shared/components/ui/Modal/Modal';
import Button from '../../../../shared/components/ui/Button';
import cn from '../../../../shared/utils/classNames';
import useAuth from '../../../../shared/hooks/useAuth';
import useTranslation from '../../../../shared/hooks/useTranslation';
import { apiCompleteOnboarding } from '../../../../shared/api/authFetch';
import { useOnboardingStore } from './useOnboardingStore';

import styles from './OnboardingModal.module.css';

export const OnboardingModal = () => {
  const { user, updateUser } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const requestTour = useOnboardingStore((state) => state.requestTour);
  const requestHelp = useOnboardingStore((state) => state.requestHelp);
  const setWelcomeModalOpen = useOnboardingStore((state) => state.setWelcomeModalOpen);

  const userId = user?.id;
  const onboardingCompletedAt = user?.onboardingCompletedAt;

  useEffect(() => {
    if (!userId) {
      setIsOpen(false);
      return;
    }

    if (onboardingCompletedAt) {
      setIsOpen(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsOpen(true);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userId, onboardingCompletedAt]);

  useEffect(() => {
    setWelcomeModalOpen(isOpen);
    return () => {
      setWelcomeModalOpen(false);
    };
  }, [isOpen, setWelcomeModalOpen]);

  const acknowledge = async () => {
    setIsOpen(false);

    if (!userId || onboardingCompletedAt) {
      return;
    }

    try {
      const response = await apiCompleteOnboarding();
      updateUser({
        onboardingCompletedAt: response.onboardingCompletedAt,
      });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const handleClose = () => {
    void acknowledge();
  };

  const handleSkip = () => {
    void acknowledge();
  };

  const handleStartTour = () => {
    setIsOpen(false);
    requestTour();
    void acknowledge();
  };

  const handleOpenHelp = () => {
    void acknowledge();
    requestHelp();
  };

  if (!userId) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('onboarding.welcomeTitle')}
      size="md"
      className={styles.onboardingModal}
    >
      <div className={styles.welcomeBody}>
        <p className={styles.welcomeLead}>{t('onboarding.welcomeLead')}</p>
        <p className={styles.welcomeText}>{t('onboarding.welcomeBody')}</p>
      </div>

      <div className={styles.footer}>
        <p className={styles.hint}>{t('onboarding.footerHint')}</p>
        <div className={styles.actions}>
          <Button
            variant="primary"
            size="sm"
            compact
            className={cn(styles.primaryAction, styles.actionButton)}
            onClick={handleStartTour}
          >
            {t('onboarding.startTour')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            compact
            className={styles.actionButton}
            onClick={handleOpenHelp}
          >
            {t('onboarding.openHelp')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            compact
            className={styles.actionButton}
            onClick={handleSkip}
          >
            {t('onboarding.skip')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
